import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useLoading } from "../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../constants/loadingKeys";
import { deleteAssessmentsNoteAsync, notesAssessmentAsync, notesPostAsync, setDeleteAssessmentNoteState, updateNotesAsync } from "../../../../app/patientSlice";
import { ERROR_STATUS, SUCCESS_STATUS } from "../../../../constants";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../constants/permissions";

export const useAssessmentNotes = () => {
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams();
  const { register, handleSubmit, reset } = useForm();
  const { hasPermission } = usePermissions();
  const canCreateAssessmentNote = hasPermission(PERMISSIONS.ASSESSMENT_NOTE_CREATE);

  const notes = useSelector((state) => state.patient.assessmentNotes);
  const detailAssessment = useSelector((state) => state.patient.detailAssessment);
  const { measurementData } = useSelector((state) => state.patient.newWoundMeasurements) || {
    loading: false,
    measurementData: null,
  };
  const deleteAssessmentNoteState = useSelector((state) => state.patient.deleteAssessmentNoteState);
  const assessmentNotesLoading = useLoading(LOADING_KEYS.ASSESSMENT_NOTE);
  const updateNoteLoading = useLoading(LOADING_KEYS.UPDATE_NOTES);
  const createNoteLoading = useLoading(LOADING_KEYS.CREATE_NOTE);

  const [woundImageLoading, setWoundImageLoading] = useState(true);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [deleteIds, setDeleteIds] = useState(null);

  //edit note
  const [editNoteId, setEditNoteId] = useState(null);
  const [editedNote, setEditedNote] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    dispatch(notesAssessmentAsync({ patientId, assessmentId }));
  }, [assessmentId, dispatch, patientId]);

  useEffect(() => {
    if (deleteAssessmentNoteState === SUCCESS_STATUS || deleteAssessmentNoteState === ERROR_STATUS || successMessage) {
      const timer = setTimeout(() => {
        dispatch(setDeleteAssessmentNoteState(null));
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [dispatch, deleteAssessmentNoteState, successMessage]);

  const doSubmit = (data) => {
    if (data?.notes) {
      const payload = {
        assessmentId,
        patientId,
        notes: data.notes.trim(),
      };
      dispatch(notesPostAsync(payload));
    }
    reset();
  };

  const handleDeleteNote = (e) => {
    e?.preventDefault();
    dispatch(deleteAssessmentsNoteAsync(patientId, assessmentId, deleteIds, setDeleteConfirmationModal));
    setDeleteIds(null);
  };

  const handleDeleteAssessmentNote = (data) => {
    setDeleteConfirmationModal(true);
    setDeleteIds({ id: data?.sourceNoteId, assessmentId: data?.sourceAssessmentId });
  };

  const handleEditClick = useCallback((note) => {
    setEditNoteId(note.sourceNoteId);
    setEditedNote(note.notes || "");
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditNoteId(null);
    setEditedNote("");
  }, []);

  const handleSaveEdit = useCallback(async () => {
    const payload = {
      patientId,
      assessmentId,
      updateId: editNoteId,
      note: editedNote,
    };
    const response = await dispatch(updateNotesAsync(payload));
    if (response) {
      setSuccessMessage(response);
      dispatch(notesAssessmentAsync({ patientId, assessmentId }));
      setEditNoteId(null);
    }
  }, [assessmentId, dispatch, editNoteId, editedNote, patientId]);

  const onKeyDown = useCallback(
    (e, id) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSaveEdit(id);
      }

      if (e.key === "Escape") {
        e.preventDefault();
        handleCancelEdit();
      }
    },
    [handleCancelEdit, handleSaveEdit]
  );

  const onEditChange = useCallback((e) => {
    setEditedNote(e?.target?.value);
  }, []);

  return {
    register,
    handleSubmit,
    reset,
    doSubmit,
    hasPermission,
    canCreateAssessmentNote,
    notes,
    detailAssessment,
    measurementData,
    createNoteLoading,
    deleteAssessmentNoteState,
    assessmentNotesLoading,
    woundImageLoading,
    setWoundImageLoading,
    deleteConfirmationModal,
    setDeleteConfirmationModal,
    deleteIds,
    handleDeleteNote,
    handleDeleteAssessmentNote,
    // edit notes
    successMessage,
    updateNoteLoading,
    editNoteId,
    editedNote,
    handleEditClick,
    handleCancelEdit,
    handleSaveEdit,
    onEditChange,
    onKeyDown,
  };
};
